import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

let isRefreshing = false;
const refreshDone$ = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastrService);

  const token = authService.getToken();
  const authReq = req.clone({
    setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  });

  return next(authReq).pipe(
    catchError(err => {
      if (err.status === 401 && !req.url.includes('auth/refresh') && !req.url.includes('auth/login') && !req.url.includes('auth/logout')) {
        return handle401(req, next, authService, router, toastService);
      }
      if (err.status === 403 && err.error?.code === 'PROFILE_INCOMPLETE') {
        router.navigate(['/completar-perfil']);
      }
      return throwError(() => err);
    })
  );
};

function handle401(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  toastService: ToastrService,
) {
  if (isRefreshing) {
    return refreshDone$.pipe(
      filter(t => t !== null),
      take(1),
      switchMap(newToken => next(req.clone({
        setHeaders: { Authorization: `Bearer ${newToken}` },
        withCredentials: true,
      })))
    );
  }

  isRefreshing = true;
  refreshDone$.next(null);

  return authService.refreshTokens().pipe(
    switchMap((res: any) => {
      isRefreshing = false;
      refreshDone$.next(res.token);
      return next(req.clone({
        setHeaders: { Authorization: `Bearer ${res.token}` },
        withCredentials: true,
      }));
    }),
    catchError(refreshErr => {
      isRefreshing = false;
      refreshDone$.next(null);
      doLogout(authService, router, toastService);
      return throwError(() => refreshErr);
    })
  );
}

function doLogout(authService: AuthService, router: Router, toastService: ToastrService) {
  authService.logout();
  router.navigate(['/login']);
  toastService.error('Tu sesión ha caducado. Ingresa nuevamente para continuar.', 'Sesión cerrada');
}
